import React, { useEffect, useState } from 'react';
import { apoliceService, ApoliceData } from '../../services/apoliceService';
import { ApoliceFormModal } from '../apolice/ApoliceFormModal';

interface RenovacaoCardProps {
  className?: string;
}

const RenovacaoCard: React.FC<RenovacaoCardProps> = ({ className = '' }) => {
  const [renovacoes, setRenovacoes] = useState<ApoliceData[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedApolice, setSelectedApolice] = useState<ApoliceData | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [expanded, setExpanded] = useState(true);
  const itemsPerPage = 3;

  useEffect(() => {
    loadRenovacoes();
  }, [currentPage]);

  const loadRenovacoes = async () => {
    try {
      setLoading(true);
      const response = await apoliceService.getRenovacoes(currentPage, itemsPerPage);
      setRenovacoes(response.data);
      setTotalPages(response.totalPages || 1);
      setTotal(response.total || 0);
    } catch (error) {
      // Error handled silently
    } finally {
      setLoading(false);
    }
  };

  const handleClienteClick = async (renovacao: ApoliceData) => {
    // Busca os dados completos da apólice antes de abrir o modal
    try {
      if (renovacao.id) {
        const response = await apoliceService.getApolices({ search: renovacao.id }, '/documentos');
        if (response.data && response.data.length > 0) {
          setSelectedApolice(response.data[0]);
        } else {
          setSelectedApolice(renovacao);
        }
      } else {
        setSelectedApolice(renovacao);
      }
    } catch (error) {
      setSelectedApolice(renovacao);
    }
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedApolice(null);
  };

  const handleSave = () => {
    handleCloseModal();
    loadRenovacoes(); // Recarrega a lista
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  // Helper para exibir o produto corretamente
  const getProdutoNome = (renovacao: ApoliceData): string => {
    // Se produto é um objeto com nome
    if (renovacao.produto && typeof renovacao.produto === 'object' && renovacao.produto.nome) {
      return renovacao.produto.nome;
    }
    // Se produto é string diretamente
    if (typeof renovacao.produto === 'string') {
      return renovacao.produto;
    }
    // Se tem produtoId como string (nome do produto)
    if (renovacao.produtoId && typeof renovacao.produtoId === 'string' && isNaN(Number(renovacao.produtoId))) {
      return renovacao.produtoId;
    }
    return 'INFORME O PRODUTO';
  };

  if (loading) {
    return (
      <div className={`bg-white rounded-lg shadow-lg p-4 h-[450px] flex flex-col ${className}`}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold text-green-700 flex items-center gap-2">
            🔄 Renovação
          </h2>
        </div>
        <div className="flex-1 flex items-center justify-center">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-green-500"></div>
          <span className="ml-2 text-gray-500 text-sm">Carregando...</span>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className={`bg-white rounded-lg shadow-lg p-4 ${expanded ? 'h-[450px]' : 'h-auto'} flex flex-col ${className}`}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
            Renovação
            <span className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded-full font-semibold">
              {total}
            </span>
          </h2>
          <div className="flex gap-2">
            <button 
              onClick={() => setExpanded(!expanded)}
              className="p-1 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded transition-colors"
              title={expanded ? 'Recolher' : 'Expandir'}
            >
              <svg 
                className={`w-4 h-4 transition-transform ${expanded ? 'rotate-180' : ''}`} 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
          </div>
        </div>

        {expanded && (
          <div className="space-y-3 flex-1 overflow-y-auto max-h-[280px]">
            {renovacoes.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-8 text-center">
                <div className="text-gray-400 text-4xl mb-2">🔄</div>
                <p className="text-gray-500 text-sm">Nenhuma renovação encontrada</p>
            </div>
          ) : (
            renovacoes.map((renovacao) => (
              <div 
                key={renovacao.id} 
                className="border border-gray-200 rounded-lg p-3 hover:bg-green-50 hover:border-green-200 transition-all duration-200 hover:shadow-sm"
              >
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {renovacao.numeroApolice || `Apólice ${renovacao.id}`}
                    </p>
                    <span className="px-2 py-0.5 bg-orange-100 text-orange-700 rounded-full text-xs font-medium">
                      {getProdutoNome(renovacao)}
                    </span>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-2 text-xs text-gray-600 mb-2">
                    <div>
                      <button
                        onClick={() => handleClienteClick(renovacao)}
                        className="text-green-600 hover:text-green-800 underline cursor-pointer"
                      >
                        {renovacao.cliente?.nome || 'Sem nome'}
                      </button>
                    </div>
                    <div>
                      <span className="font-medium">Fim Vigência:</span>
                      <span className="ml-1">
                        {renovacao.fimVigencia
                          ? new Date(renovacao.fimVigencia).toLocaleDateString('pt-BR')
                          : '-'}
                      </span>
                    </div>
                  </div>

                  {expanded && (
                    <div className="mt-2 space-y-1 text-xs text-gray-600">
                      <p>
                        <span className="font-medium">Seguradora:</span> {renovacao.seguradora?.nome || 'N/A'}
                      </p>
                      <p>
                        <span className="font-medium">Valor Prêmio:</span> {renovacao.valorPremio ? `R$ ${Number(renovacao.valorPremio).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}` : 'N/A'}
                      </p>
                    </div>
                  )}
                </div>
                
                <div className="ml-2 flex-shrink-0">
                  <div className="w-2 h-2 rounded-full bg-green-500"></div>
                </div>
              </div>
              </div>
            ))
          )}
        </div>
        )}

        {/* Paginação */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-200">
            <button
              onClick={handlePreviousPage}
              disabled={currentPage === 1}
              className="px-3 py-1 text-xs rounded bg-gray-100 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              ← Anterior
            </button>
            <span className="text-xs text-gray-600 bg-gray-100 px-2 py-1 rounded">
              {currentPage} de {totalPages}
            </span>
            <button
              onClick={handleNextPage}
              disabled={currentPage === totalPages}
              className="px-3 py-1 text-xs rounded bg-gray-100 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Próxima →
            </button>
          </div>
        )}
      </div>

      {selectedApolice && (
        <ApoliceFormModal
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          onSave={handleSave}
          apolice={selectedApolice}
          isEditing={true}
          isFromRenovationCard={true}
        />
      )}
    </>
  );
};

export default RenovacaoCard;
